import { useTranslation } from 'react-i18next';
import {
  FreelancerFilterModal,
  createServiceGroupBy,
} from '@pigeons/shared-search-utility/freelancer';
import { Button } from '../common/Button';
import FullBlockingModal from '../common/FullBlockingModal';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import { useCitySelector } from '../../hooks/useCitySelector';
import { useIsFutureEnabled } from '../../hooks/useIsFeatureEnabled';
import { ServiceCategory } from '../../types/service';
import { City } from '../../types/profile';
import { languageOptions } from '../../utils/languagesUtils';
import type { ReactNode } from 'react';

interface FilterModalProps {
  className?: string;
  rating: number;
  selectedLanguages: string[];
  selectedServices: ServiceCategory[];
  selectedCities: City[];
  proximity: number;
  isBlocking?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  setSelectedLanguages: (languages: string[]) => void;
  setSelectedServices: (services: ServiceCategory[]) => void;
  setSelectedCities: (cities: City[]) => void;
  setProximity: (proximity: number) => void;
  setRating: (rating: number) => void;
}

const FilterModalButton = ({
  className,
  onClick,
  children,
}: {
  className?: string;
  onClick?: () => void;
  children?: ReactNode;
}) => (
  <Button className={className} onClick={onClick}>
    {children ?? null}
  </Button>
);

export default function FilterModal(props: FilterModalProps) {
  const { t } = useTranslation(['freelancers', 'common']);
  const isReviewsEnabled = useIsFutureEnabled('reviews');
  const isProximitySearchEnabled = useIsFutureEnabled('proximitySearch');

  const {
    options: serviceCategoryOptions,
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
  } = useServiceCategorySelector(props.selectedServices);

  const {
    options: cityOptions,
    selectedOptions: selectedCityOptions,
    setSelectedOptions: setSelectedCityOptions,
  } = useCitySelector(props.selectedCities);

  const groupBy = createServiceGroupBy((groupKey: string) =>
    t(`serviceGroups.${groupKey}`, { ns: 'common' })
  );

  return (
    <FreelancerFilterModal
      {...props}
      selectedServices={selectedServiceCategories}
      selectedCities={selectedCityOptions}
      setSelectedServices={items => {
        props.setSelectedServices(items);
        setSelectedServiceCategories(items);
      }}
      setSelectedCities={items => {
        props.setSelectedCities(items);
        setSelectedCityOptions(items);
      }}
      serviceCategoryOptions={serviceCategoryOptions}
      cityOptions={cityOptions}
      messages={{
        filterBy: t('filterBy', { ns: 'freelancers' }),
        languagesSpoken: t('form_fields.languagesSpoken', { ns: 'common' }),
        selectService: t('form_fields.selectService', { ns: 'common' }),
        addService: t('addService', { ns: 'freelancers' }),
        city: t('form_fields.city', { ns: 'common' }),
        addCity: t('addCity', { ns: 'freelancers' }),
        rating: t('rating', { ns: 'freelancers' }),
        proximity: t('proximity', { ns: 'freelancers' }),
        maximum: t('maximum', { ns: 'freelancers' }),
        showResults: t('showResults', { ns: 'freelancers' }),
        close: t('common:form_actions.close'),
      }}
      dropdownUiStrings={{
        removeAria: t('remove', { ns: 'common' }),
        loading: t('loading', { ns: 'common' }),
        noResults: t('noResults', { ns: 'common' }),
        saving: t('common:form_actions.saving', { ns: 'common' }),
        formAdd: t('common:form_actions.add', { ns: 'common' }),
      }}
      groupBy={groupBy}
      reviewsEnabled={isReviewsEnabled}
      proximitySearchEnabled={isProximitySearchEnabled}
      languageOptions={languageOptions}
      Button={FilterModalButton}
      FullBlockingModal={FullBlockingModal}
    />
  );
}
